import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, deleteDoc, collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { setDirtyFlag } from '../safeNavigate';

import PageWrapperAuto from '../components/PageWrapperAuto';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ConfirmLeaveModal from '../components/ConfirmLeaveModal';
import CustomTagInput from '../components/CustomTagInput';
import { useUserPrefs } from '../UserPrefsProvider';
import { loadSetHybrid } from '../utils/loadSetHybrid';
import dexie from '../offlineDB';
import { addPendingWrite, flushPendingWrites } from '../utils/flushPendingWrites';
import { normalizeTagsArray, normalizeTag, getNormalizedGenreOptions } from '../utils/normalize';

export default function EditProgramPage() {
  const { inputStyle } = useUI();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: programId } = useParams();

  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [rating, setRating] = useState('');
  const [genre, setGenre] = useState('');
  const [slots, setSlots] = useState([]);
  const [newSlotName, setNewSlotName] = useState('');
  const [customTags, setCustomTags] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [wasCreatedEmpty, setWasCreatedEmpty] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingLeaveAction, setPendingLeaveAction] = useState(null);
  const [editingSlotIndex, setEditingSlotIndex] = useState(null);

  const { prefs, labels, modeGenres } = useUserPrefs();
  const [allTunes, setAllTunes] = useState([]);

  // Load all tunes for genre options
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      collection(db, 'users', user.uid, 'tunes'),
      snapshot => setAllTunes(snapshot.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [user]);

  const currentMode = prefs?.appMode || 'tunes';
  const genreOptions = React.useMemo(() => {
    const defaults = labels?.defaultGenres || ['Irish', 'Old-Time', 'Bluegrass'];
    const inUse = allTunes.filter(t => currentMode === 'all' || (t.mode || 'tunes') === currentMode).map(t => t.genre).filter(Boolean);
    return getNormalizedGenreOptions([...defaults, ...(modeGenres || []), ...inUse]);
  }, [allTunes, modeGenres, labels?.defaultGenres, currentMode]);

  useEffect(() => {
    if (!user || !programId) return;

    const load = async () => {
      try {
        if (navigator.onLine) {
          await flushPendingWrites(user);
        }
        const data = await loadSetHybrid(user, programId);
        if (data) {
          setTitle(data.title || '');
          setNotes(data.notes || '');
          setRating(data.rating || '');
          setGenre(data.genre || '');
          setSlots(data.slots || []);
          setCustomTags(data.customTags || []);

          if (!data.title && (!data.slots || data.slots.length === 0)) {
            setWasCreatedEmpty(true);
          }
        }
      } catch (err) {
        console.error('Error loading program:', err);
      }
    };

    load();
  }, [user, programId]);

  // Dirty flag sync
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  useEffect(() => { setDirtyFlag(isDirty); }, [isDirty]);
  useEffect(() => { return () => { setIsDirty(false); setDirtyFlag(false); }; }, []);

  const handleAddSlot = () => {
    const name = newSlotName.trim();
    if (!name) return;
    setSlots(prev => [...prev, { name, order: prev.length }]);
    setNewSlotName('');
    setIsDirty(true);
  };

  const handleRemoveSlot = (index) => {
    setSlots(prev => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i })));
    setIsDirty(true);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(slots);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setSlots(reordered.map((s, i) => ({ ...s, order: i })));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!user) return;

    const updatedData = {
      title: title.trim() || 'Untitled Program',
      notes: notes.trim(),
      slots: slots.filter(s => s.name.trim()),
      isProgram: true,
      genre,
      rating,
      customTags: normalizeTagsArray(customTags),
      updatedAt: new Date().toISOString()
    };

    const ref = doc(db, 'users', user.uid, 'sets', programId);

    // Sync tags to user preferences
    try {
      const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
      const prefsSnap = await getDoc(prefsRef);
      const prefsData = prefsSnap.exists() ? prefsSnap.data() : {};
      const existingPrefsTags = Array.isArray(prefsData.customTags) ? prefsData.customTags : [];

      const mergedTags = Array.from(new Set([
        ...normalizeTagsArray(customTags || []),
        ...existingPrefsTags.map(normalizeTag)
      ]));

      if (navigator.onLine) {
        await updateDoc(prefsRef, { customTags: mergedTags });
      } else {
        await addPendingWrite({
          id: 'settings',
          type: 'update',
          collection: 'preferences',
          docId: 'settings',
          data: { customTags: mergedTags }
        });
      }
    } catch (err) {
      console.warn('Could not sync tags to user preferences:', err);
    }

    // Save the program
    try {
      if (navigator.onLine) {
        await updateDoc(ref, updatedData);
        await dexie.sets.put({ id: programId, ...updatedData });
      } else {
        await dexie.sets.put({ id: programId, ...updatedData });
        await addPendingWrite({
          id: programId,
          type: 'update',
          collection: 'sets',
          docId: programId,
          data: { id: programId, ...updatedData }
        });
      }

      setIsDirty(false);
      navigate(`/sets/${programId}`);
      alert('Program saved');
    } catch (err) {
      console.error('Error saving program:', err);
      alert('Failed to save program');
    }
  };

  const handleCancel = async () => {
    const cleanup = async () => {
      if (wasCreatedEmpty && user && programId) {
        const ref = doc(db, 'users', user.uid, 'sets', programId);
        try {
          if (navigator.onLine) {
            await deleteDoc(ref);
            await dexie.sets.delete(programId);
          } else {
            await dexie.sets.delete(programId);
            await addPendingWrite({
              id: programId,
              type: 'delete',
              collection: 'sets',
              docId: programId
            });
          }
        } catch (err) {
          console.error('Failed to delete empty program:', err);
        }
      }
      navigate('/sets');
    };

    if (isDirty) {
      setPendingLeaveAction(() => cleanup);
      setShowLeaveModal(true);
    } else {
      await cleanup();
    }
  };

  return (
    <PageWrapperAuto>
      <h2>Edit Program</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '400px', margin: '0 auto 20px' }}>
        <input
          style={inputStyle}
          value={title}
          onChange={e => { setTitle(e.target.value); setIsDirty(true); }}
          placeholder="Program title"
        />

        <select
          style={inputStyle}
          value={genre}
          onChange={e => { setGenre(e.target.value); setIsDirty(true); }}
        >
          <option value="">{labels?.genreLabel || 'Genre'}</option>
          {genreOptions.map((g, i) => (
            <option key={i} value={g}>{g}</option>
          ))}
        </select>

        <textarea
          style={inputStyle}
          value={notes}
          onChange={e => { setNotes(e.target.value); setIsDirty(true); }}
          placeholder="Notes (optional)"
          rows={3}
        />

        <select
          style={inputStyle}
          value={rating}
          onChange={e => { setRating(e.target.value); setIsDirty(true); }}
        >
          <option value="">Rating</option>
          <option value="&#9733;">&#9733;</option>
          <option value="&#9733;&#9733;">&#9733;&#9733;</option>
          <option value="&#9733;&#9733;&#9733;">&#9733;&#9733;&#9733;</option>
          <option value="&#9733;&#9733;&#9733;&#9733;">&#9733;&#9733;&#9733;&#9733;</option>
          <option value="&#9733;&#9733;&#9733;&#9733;&#9733;">&#9733;&#9733;&#9733;&#9733;&#9733;</option>
        </select>

        <div>
          <h4 style={{ marginBottom: '0.25rem' }}>Tags</h4>
          <CustomTagInput
            tags={customTags}
            setTags={(tags) => { setCustomTags(tags); setIsDirty(true); }}
            suggestions={prefs?.customTags || []}
            inputStyle={inputStyle}
          />
        </div>
      </div>

      <h3>Slots in Program</h3>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 0 }}>
        Define the order of items. When you use this program, you'll pick a tune for each slot.
      </p>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="slotList">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                maxHeight: '400px',
                overflowY: 'auto',
                borderRadius: '8px',
                padding: '6px',
                backgroundColor: 'var(--input-bg)',
                minHeight: '40px',
                maxWidth: '400px',
                margin: '0 auto'
              }}
            >
              {slots.length === 0 && (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  No slots yet. Add slots below.
                </div>
              )}
              {slots.map((slot, index) => (
                <Draggable key={`slot-${index}`} draggableId={`slot-${index}`} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: 'var(--item-bg, #f9f9f9)',
                        color: 'var(--text-color, #000)',
                        padding: '8px',
                        borderBottom: '1px solid #ccc',
                        ...provided.draggableProps.style
                      }}
                    >
                      <span style={{ flex: 1 }}>
                        <span style={{ color: 'var(--text-muted)', marginRight: '8px', fontSize: '0.85rem' }}>{index + 1}.</span>
                        {editingSlotIndex === index ? (
                          <input
                            value={slot.name}
                            onChange={e => {
                              const val = e.target.value;
                              setSlots(prev => prev.map((s, i) => i === index ? { ...s, name: val } : s));
                              setIsDirty(true);
                            }}
                            onBlur={() => setEditingSlotIndex(null)}
                            onKeyDown={e => { if (e.key === 'Enter') setEditingSlotIndex(null); }}
                            autoFocus
                            style={{ ...inputStyle, padding: '2px 6px', fontSize: 'inherit', width: '60%' }}
                            onClick={e => e.stopPropagation()}
                          />
                        ) : (
                          <span
                            onClick={(e) => { e.stopPropagation(); setEditingSlotIndex(index); }}
                            style={{ cursor: 'text' }}
                            title="Click to edit"
                          >
                            {slot.name}
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => handleRemoveSlot(index)}
                        style={{ ...inputStyle, marginLeft: '10px', padding: '2px 8px' }}
                      >
                        &times;
                      </button>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '12px', maxWidth: '400px', margin: '12px auto 0' }}>
        <input
          style={{ ...inputStyle, flex: 1 }}
          value={newSlotName}
          onChange={e => setNewSlotName(e.target.value)}
          placeholder="Slot name (e.g. Lecha Dodi)"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSlot(); } }}
        />
        <button style={inputStyle} onClick={handleAddSlot}>+ Add Slot</button>
      </div>

      <div className="save-cancel-row">
        <button className="btn-subtle btn-commit" onClick={handleSave}>Save Program</button>
        <button
          className="btn-subtle"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>

      <ConfirmLeaveModal
        open={showLeaveModal}
        onCancel={() => setShowLeaveModal(false)}
        onConfirm={() => {
          setShowLeaveModal(false);
          setIsDirty(false);
          if (pendingLeaveAction) pendingLeaveAction();
        }}
      />
    </PageWrapperAuto>
  );
}
